import React, { useState, useRef, useEffect } from 'react';
import { useOpenAI } from '../../hooks/useOpenAI';
import Icon from '../AppIcon';
import Button from '../ui/Button';

const AIAssistantChat = ({ 
  className = '',
  placeholder = 'Ask me about product matching, pricing, or analysis...',
  systemPrompt = 'You are an AI assistant specializing in product matching, price analysis, and supply chain optimization. Provide helpful, accurate insights.',
  onResponse = null 
}) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'assistant',
      content: 'Hello! I\'m your AI assistant for product matching and price analysis. How can I help you today?',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const { 
    loading,
    error,
    streamingResponse,
    chatCompletion,
    streamingChat,
    resetStreamingResponse,
    clearError
  } = useOpenAI();

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingResponse]);

  const handleSendMessage = async () => {
    if (!inputValue?.trim() || loading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputValue?.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsStreaming(true);
    resetStreamingResponse();

    try {
      // Start streaming response
      await streamingChat(userMessage?.content, systemPrompt);
      
      // Add the complete response to messages
      const assistantMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: streamingResponse,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      // Call onResponse callback if provided
      if (onResponse) {
        onResponse(assistantMessage?.content);
      }
    } catch (err) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: 'I apologize, but I encountered an error processing your request. Please try again.',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsStreaming(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearChat = () => {
    setMessages([
      {
        id: Date.now(),
        type: 'assistant',
        content: 'Chat cleared. How can I help you?',
        timestamp: new Date()
      }
    ]);
    clearError();
    resetStreamingResponse();
    inputRef?.current?.focus();
  };

  return (
    <div className={`flex flex-col h-full bg-card border border-border rounded-lg ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
            <Icon name="Bot" size={16} color="white" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">AI Assistant</h3>
            <p className="text-xs text-muted-foreground">
              {loading ? 'Thinking...' : 'Ready to help'}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearChat}
          className="text-muted-foreground hover:text-foreground"
        >
          <Icon name="Trash2" size={16} />
        </Button>
      </div>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages?.map((message) => (
          <div
            key={message?.id}
            className={`flex ${message?.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-lg p-3 ${
                message?.type === 'user' ?'bg-primary text-primary-foreground'
                  : message?.isError
                  ? 'bg-destructive/10 text-destructive border border-destructive/20' :'bg-muted text-muted-foreground'
              }`}
            >
              <div className="text-sm whitespace-pre-wrap">
                {message?.content}
              </div>
              <div className="text-xs opacity-70 mt-1">
                {message?.timestamp?.toLocaleTimeString()}
              </div>
            </div>
          </div>
        ))}

        {/* Streaming Response */}
        {isStreaming && streamingResponse && (
          <div className="flex justify-start">
            <div className="max-w-[80%] rounded-lg p-3 bg-muted text-muted-foreground">
              <div className="text-sm whitespace-pre-wrap">
                {streamingResponse}
                <span className="inline-block w-2 h-4 bg-primary ml-1 animate-pulse" />
              </div>
            </div>
          </div>
        )}

        {/* Loading Indicator */}
        {loading && !streamingResponse && (
          <div className="flex justify-start">
            <div className="max-w-[80%] rounded-lg p-3 bg-muted text-muted-foreground">
              <div className="flex items-center space-x-2">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-primary rounded-full animate-bounce" />
                  <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                  <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                </div>
                <span className="text-sm">AI is thinking...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
      {/* Error Display */}
      {error && (
        <div className="p-4 border-t border-border">
          <div className="flex items-center space-x-2 text-destructive text-sm">
            <Icon name="AlertCircle" size={16} />
            <span>{error}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearError}
              className="ml-auto"
            >
              <Icon name="X" size={14} />
            </Button>
          </div>
        </div>
      )}
      {/* Input */}
      <div className="p-4 border-t border-border">
        <div className="flex space-x-2">
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e?.target?.value)}
              onKeyPress={handleKeyPress}
              placeholder={placeholder}
              className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              rows={1}
              style={{
                minHeight: '40px',
                maxHeight: '120px',
                height: 'auto'
              }}
              onInput={(e) => {
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e?.target?.scrollHeight, 120) + 'px';
              }}
              disabled={loading}
            />
          </div>
          <Button
            onClick={handleSendMessage}
            disabled={!inputValue?.trim() || loading}
            size="sm"
            className="px-3"
          >
            {loading ? (
              <Icon name="Loader2" size={16} className="animate-spin" />
            ) : (
              <Icon name="Send" size={16} />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AIAssistantChat;